import { Icon, StarMark } from "@core/icons";
import { tokens } from "@core/theme/tokens";
import { ImageWithPlaceholder } from "@core/media";

export interface WelcomePreviewChip {
  readonly icon: string;
  readonly title: string;
  readonly description: string;
}

// Reproduce `Onboarding` (star-circle-app.jsx:1474-1560), variante `centered`
// —`SCREEN_LAYOUT["onboarding"] = "centered"` (:3967) es la que se ve en la
// baseline `portal-welcome-screen-default@1440x900.png`. El prototipo embebe
// `<window.PhoneApp initialRoute="onboarding" />`; acá no existe esa app, así
// que este componente reproduce únicamente lo visible de esa pantalla. El
// titular, el párrafo fijo y la marca "STAR CIRCLE" NO son administrables
// (SPEC-205 §2/§9) — viven acá como texto fijo, no en el formulario.
export function WelcomePreview({
  imageUrl,
  chips,
}: {
  imageUrl: string;
  chips: readonly WelcomePreviewChip[];
}) {
  return (
    <div
      // SPEC-205, corrección de scroll: antes `height: 874` fijo +
      // `overflow: "hidden"` recortaba cualquier contenido que excediera
      // 874px de forma silenciosa (ej. una descripción de chip más larga
      // que envuelve a una línea extra). El marco de 700px que envuelve
      // este lienzo (`WelcomeContentScreen.tsx`) es ahora el único que
      // recorta y desplaza — el lienzo crece con su contenido real y no
      // se corta a sí mismo.
      style={{
        position: "relative",
        width: 402,
        minHeight: 874,
        background: tokens.surface.bg,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 312,
          zIndex: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        {/* PLAN revisión-live-2026-09-22 #23: antes, sin `imageUrl`, este
          bloque renderizaba `null` y solo quedaba el degradado oscuro de
          abajo — se veía roto, no vacío. `ImageWithPlaceholder` ocupa el
          mismo lugar (mismo `objectPosition`, sin la máscara: no hay imagen
          que recortar) y el degradado siguiente sigue encima, sin romper el
          texto superpuesto. */}
        <ImageWithPlaceholder
          src={imageUrl}
          iconSize={26}
          imageStyle={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center 58%",
            WebkitMaskImage:
              "linear-gradient(180deg, #000 0%, #000 58%, transparent 100%)",
            maskImage:
              "linear-gradient(180deg, #000 0%, #000 58%, transparent 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(10,10,11,0.30) 0%, rgba(10,10,11,0) 26%, rgba(10,10,11,0.70) 100%)",
          }}
        />
      </div>
      <div
        style={{
          position: "relative",
          zIndex: 1,
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: "0 26px 30px",
        }}
      >
        <div
          style={{
            height: 312,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingBottom: 4,
          }}
        >
          <div style={{ position: "relative" }}>
            <div
              style={{
                position: "absolute",
                inset: -26,
                background:
                  "radial-gradient(circle, rgba(159,176,166,0.12), transparent 70%)",
                filter: "blur(6px)",
              }}
            />
            <StarMark size={58} ring stroke={1} />
          </div>
          <div
            className="display"
            style={{
              fontSize: 13,
              letterSpacing: "0.38em",
              textTransform: "uppercase",
              color: tokens.text.secondary,
              marginTop: 14,
              fontFamily: tokens.font.ui,
              fontWeight: 600,
            }}
          >
            Star Circle
          </div>
        </div>
        <h1
          className="display"
          style={{ fontSize: 34, margin: "26px 0 0", maxWidth: 300 }}
        >
          Bienvenido a un círculo{" "}
          <span className="silver-text" style={{ fontStyle: "italic" }}>
            que pocos conocen
          </span>
        </h1>
        <p
          style={{
            color: tokens.text.secondary,
            font: `400 14px/1.6 ${tokens.font.ui}`,
            maxWidth: 280,
            margin: "18px 0 0",
          }}
        >
          El programa exclusivo para propietarios Mercedes-Benz de Teojama
          Comercial.
        </p>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 11,
            margin: "32px 0 30px",
          }}
        >
          {chips.map((chip, index) => (
            <div
              key={index}
              className="card-flat"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "15px 16px",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 13,
                  background: tokens.surface.surface2,
                  border: `1px solid ${tokens.surface.line}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: "none",
                  color: tokens.silver.flat,
                }}
              >
                <Icon name={chip.icon} size={20} stroke={1.4} />
              </div>
              <div>
                <div
                  style={{
                    font: `600 13.5px ${tokens.font.ui}`,
                    color: tokens.text.primary,
                  }}
                >
                  {chip.title}
                </div>
                {/* Defecto de accesibilidad preexistente en el prototipo
                  (`star-circle-app.jsx:1537`, `var(--text3)` sobre el fondo
                  de `.card-flat`): contraste 3.57:1, bajo el 4.5:1 mínimo de
                  WCAG 2 AA para texto normal (axe `color-contrast`,
                  agent-os/OBSERVATIONS.md 2026-09-19). `tokens.text.secondary`
                  es el token inmediato siguiente en la escala (7.76:1 contra
                  este mismo fondo) — ya usado en esta misma pantalla para el
                  párrafo introductorio y la marca "Star Circle", así que no
                  introduce un tono nuevo. */}
                <div
                  style={{
                    font: `400 11.5px/1.4 ${tokens.font.ui}`,
                    color: tokens.text.secondary,
                    marginTop: 2,
                  }}
                >
                  {chip.description}
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* `OnboardCTAs` (star-circle-app.jsx:1565-1575): decorativo acá, no
          navega — es una vista previa, no la app real. Sin `disabled`: el
          atributo altera el look nativo del botón en algunos navegadores
          (mismo look, sin efecto de navegación) — `tabIndex={-1}` más
          `aria-hidden` lo saca del foco y de la accesibilidad sin tocar un
          solo píxel. */}
        <div style={{ width: "100%", marginTop: "auto" }}>
          <div
            aria-hidden
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            <button
              type="button"
              className="btn btn-primary"
              tabIndex={-1}
              style={{ cursor: "default" }}
            >
              Activar mi membresía
            </button>
            <button
              type="button"
              className="btn-link"
              tabIndex={-1}
              style={{ cursor: "default" }}
            >
              Ya soy miembro · <b>Ingresar</b>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
